// Vendor Dashboard — strictly Vendor + Vendor Purchase focused (no stock,
// inventory, or sales-revenue widgets by design). Wired to the Vendor-specific
// backend aggregation queries via ONE combined GraphQL round trip
// (getVendorDashboardDataAction), following the same pattern as
// DashboardScreen.tsx / SalesDashboardScreen.tsx / CustomerDashboardScreen.tsx.
//
// Dropped vs. the original brief (no backing data in the current schema):
// tbl_vendorpurchase is a flat "amount spent" ledger with no paid/pending
// split anywhere in the schema (confirmed against vendor, tbl_vendorpurchase,
// billing/billingitems, tbl_procurement) — every purchase row is treated as
// fully paid, so Total Billing == Paid Amount == Total Purchase Amount and
// Pending Amount is always 0. The Billing Status (Paid vs Pending) chart was
// removed from this screen since it would only ever render 100% Paid. For the
// same reason, "Recent Vendor Bills" is not a separate widget from "Recent
// Vendor Purchases" — both would read the exact same rows, so this screen
// shows a single, paginated table.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import { Building2, UserCheck, UserX, Receipt, ShoppingCart, X } from "lucide-react";
import AppConstant from "../../../Globals/AppConstants";
import { getVendorDashboardDataAction, vendorDropDownListAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { StatCard } from "../../../shared/stat-card";
import { DataTable } from "../../../shared/data-table";
import { Skeleton } from "ui/skeleton";
import { Button } from "ui/button";
import { Combobox } from "ui/combobox";
import { ChartCard } from "./ChartCard";
import { TrendAreaChart } from "./TrendAreaChart";
import { GroupedBarChart } from "./GroupedBarChart";

const inr = (v: any) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);
const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);
const PAGE_SIZE = 10;

// Same tabbed-table convention as CustomDashboardScreen's rankings section —
// one table area, switched by tab, instead of two full tables stacked below
// each other.
const VENDOR_TABLES_TABS = [
  { key: "recentPurchases", label: "Recent Vendor Purchases" },
  { key: "purchaseVsSales", label: "Purchase vs Sales by Vendor" },
] as const;
type VendorTablesTabKey = (typeof VENDOR_TABLES_TABS)[number]["key"];

const VendorDashboardScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getVendorDashboardData, getVendorDashboardDataLoad, genVendorDropDownListData } = apparelReducerState;

  const [vendorId, setVendorId] = React.useState<string>("");
  const [page, setPage] = React.useState(1);
  const [vendorTablesTab, setVendorTablesTab] = React.useState<VendorTablesTabKey>("recentPurchases");
  const [purchaseVsSalesPage, setPurchaseVsSalesPage] = React.useState(1);

  const vendorOptions = (genVendorDropDownListData?.getVendorDropDownList || []).map((v: any) => ({
    value: String(v?.vendorId),
    label: v?.name,
  }));

  const fetchDashboard = React.useCallback(
    (selectedVendorId?: string, selectedPage?: number) => {
      const p = selectedPage || 1;
      dispatch(
        getVendorDashboardDataAction({
          vendorId: selectedVendorId || "",
          fromDate: "",
          toDate: "",
          offset: (p - 1) * PAGE_SIZE,
        })
      );
    },
    [dispatch]
  );

  React.useEffect(() => {
    dispatch(vendorDropDownListAction());
    fetchDashboard(vendorId, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVendorChange = (nextVendorId: string) => {
    setVendorId(nextVendorId);
    setPage(1);
    fetchDashboard(nextVendorId, 1);
  };

  const onPageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchDashboard(vendorId, nextPage);
  };

  const summary = getVendorDashboardData?.getVendorSummary;
  const loading = getVendorDashboardDataLoad && !summary;
  const chartsLoading = getVendorDashboardDataLoad && !getVendorDashboardData;

  const purchaseByVendorTrend = (getVendorDashboardData?.getPurchaseAmountByVendor || []).map((r: any) => ({
    date: r.date,
    value: Number(r.amount) || 0,
  }));

  const purchaseVsSalesTrend = (getVendorDashboardData?.getPurchaseVsSalesTrend || []).map((r: any) => ({
    label: r.date ? moment(r.date).format("MMM YYYY") : "-",
    purchase: Number(r.purchaseAmount) || 0,
    sales: Number(r.salesAmount) || 0,
  }));

  const purchaseVsSalesRows = getVendorDashboardData?.getPurchaseVsSalesByVendor || [];
  const pagedPurchaseVsSalesRows = purchaseVsSalesRows.slice(
    (purchaseVsSalesPage - 1) * PAGE_SIZE,
    purchaseVsSalesPage * PAGE_SIZE
  );

  const recentPurchases = getVendorDashboardData?.getRecentVendorPurchases?.vendorPurchaseDetails || [];
  const recentPurchasesPage = getVendorDashboardData?.getRecentVendorPurchases?.page;

  const recentPurchasesCsvRows = React.useMemo(
    () => recentPurchases.map((r: any) => ({
      ...r,
      purchaseDate_fmt: r.purchaseDate ? moment(r.purchaseDate).format("DD-MMM-YYYY") : "-",
      purchaseAmount: Math.round(Number(r.purchaseAmount) || 0),
    })),
    [recentPurchases]
  );

  // A fresh fetch (new date range/vendor filter) can shrink the list below
  // whatever page was previously selected — reset back to page 1 so the table
  // never renders empty from being stranded past the new last page.
  React.useEffect(() => {
    setPurchaseVsSalesPage(1);
  }, [getVendorDashboardData]);

  const recentPurchasesColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "vendorName", header: "Vendor", cell: ({ row }) => row.original?.vendorName },
      { id: "invoiceId", header: "Invoice #", cell: ({ row }) => row.original?.invoiceId ? (
        <button type="button" onClick={() => navigate("/vendor-purchase", { state: { auditRecordId: row.original.vendorPurchaseId, auditSearchCode: row.original.invoiceId } })} className="text-primary hover:underline">
          {row.original.invoiceId}
        </button>
      ) : "-" },
      {
        id: "purchaseDate",
        header: "Purchase Date",
        cell: ({ row }) => (row.original?.purchaseDate ? moment(row.original.purchaseDate).format("DD-MMM-YYYY") : "-"),
      },
      {
        id: "purchaseAmount",
        header: "Amount",
        cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.purchaseAmount)}</span>,
      },
    ],
    [navigate]
  );

  const purchaseVsSalesColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "vendorName", header: "Vendor Name", cell: ({ row }) => row.original?.vendorName },
      {
        id: "purchaseAmount",
        header: "Total Purchase Value",
        cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.purchaseAmount)}</span>,
      },
      {
        id: "salesAmount",
        header: "Total Sales Value",
        cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.salesAmount)}</span>,
      },
      {
        id: "difference",
        header: "Difference / Profit",
        cell: ({ row }) => {
          const diff = Number(row.original?.difference) || 0;
          return (
            <span className={`tabular-nums ${diff >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(diff)}</span>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="5" />

      <div className="mb-4 flex items-center gap-1 border-b">
        <button type="button" className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground">
          {AppConstant.menus.vendorDashboard}
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Showing all-time data.</p>
        <div className="flex items-center gap-1">
          <Combobox
            options={vendorOptions}
            value={vendorId || undefined}
            onValueChange={onVendorChange}
            placeholder="All Vendors"
            searchPlaceholder="Search vendor..."
            className="w-[220px]"
          />
          {vendorId && (
            <Button variant="ghost" size="icon" aria-label="Clear vendor filter" onClick={() => onVendorChange("")}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)
        ) : (
          <>
            <StatCard label="Total Vendors" value={num(summary?.totalVendors)} icon={Building2} />
            <StatCard label="Active Vendors" value={num(summary?.activeVendors)} icon={UserCheck} />
            <StatCard label="Inactive Vendors" value={num(summary?.inactiveVendors)} icon={UserX} />
            <StatCard label="Total No. of Bills" value={num(summary?.totalBills)} icon={Receipt} />
            <StatCard
              label="Total Purchase Amount"
              value={inr(summary?.totalPurchaseAmount)}
              icon={ShoppingCart}
              valueClassName="overflow-visible whitespace-normal break-words text-lg leading-tight sm:text-xl"
            />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Purchase Amount by Month" loading={chartsLoading} isEmpty={!purchaseByVendorTrend.length}>
          <TrendAreaChart data={purchaseByVendorTrend} valueFormatter={inr} granularity="month" />
        </ChartCard>
        <ChartCard title="Purchase vs Sales by Month" loading={chartsLoading} isEmpty={!purchaseVsSalesTrend.length}>
          <GroupedBarChart
            data={purchaseVsSalesTrend}
            series={[
              { key: "purchase", label: "Purchase", color: "hsl(var(--primary))" },
              { key: "sales", label: "Sales", color: "hsl(221 83% 63%)" },
            ]}
            valueFormatter={inr}
          />
        </ChartCard>
      </div>

      {/* Table */}
      <div className="mb-2 flex flex-wrap gap-1 border-b">
        {VENDOR_TABLES_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setVendorTablesTab(tab.key)}
            className={
              vendorTablesTab === tab.key
                ? "border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
                : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      {vendorTablesTab === "recentPurchases" ? (
        <DataTable
          columns={recentPurchasesColumns}
          data={recentPurchases}
          loading={chartsLoading}
          csvExport={{ fileName: `recent-vendor-purchases_${moment().format("YYYY-MM-DD")}`, rows: recentPurchasesCsvRows, columns: [{ key: "vendorName", header: "Vendor" }, { key: "invoiceId", header: "Invoice #" }, { key: "purchaseDate_fmt", header: "Purchase Date" }, { key: "purchaseAmount", header: "Amount" }] }}
          pagination={{
            page: recentPurchasesPage?.currentPage || page,
            total: recentPurchasesPage?.totalCount || 0,
            pageSize: PAGE_SIZE,
            onChange: onPageChange,
          }}
        />
      ) : (
        <DataTable
          columns={purchaseVsSalesColumns}
          data={pagedPurchaseVsSalesRows}
          loading={chartsLoading}
          csvExport={{ fileName: `purchase-vs-sales-by-vendor_${moment().format("YYYY-MM-DD")}`, rows: purchaseVsSalesRows, columns: [{ key: "vendorName", header: "Vendor Name" }, { key: "purchaseAmount", header: "Total Purchase Value" }, { key: "salesAmount", header: "Total Sales Value" }, { key: "difference", header: "Difference / Profit" }] }}
          pagination={{
            page: purchaseVsSalesPage,
            total: purchaseVsSalesRows.length,
            pageSize: PAGE_SIZE,
            onChange: setPurchaseVsSalesPage,
          }}
        />
      )}
    </div>
  );
};

export default VendorDashboardScreen;
